import * as React from 'react'
import { graphql, useStaticQuery, Link } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { Card, Button } from 'react-bootstrap'
import { UserType } from '@type/component-data-types.d'
import ExternalLink from '@components/link/ExternalLink/ExternalLink'
import PartnerCta from '@components/PartnerCta/PartnerCta'
import constant from '@assets/utility/constant'

type Props = {
  userType: UserType
}

const useGatsbyImage = (status: boolean) => status
// markup
const SideBar = (props: Props): React.JSX.Element => {
  const data = useStaticQuery(graphql`
    query {
      dataJson {
        corporateLinkData {
          path
          name
          key
          useLinkApi
        }
        partnerLinkData {
          path
          name
          key
          useLinkApi
        }
        partnerTermsPagePath
        clientContact
        clientDocumentDownload
        contact
        sideBar {
          bottomBanner {
            clientBanner {
              useLinkApi
              useGatsbyImage
              imagePath
              name
              pageUrl
              localImage {
                childImageSharp {
                  gatsbyImageData(formats: WEBP)
                }
              }
            }
            partnerBanner {
              useLinkApi
              imagePath
              name
              pageUrl
            }
          }
        }
      }
      allFile(
        filter: { name: { regex: "/ic_line|ic_facebook|ic_instagram|ic_x/" } }
      ) {
        edges {
          node {
            publicURL
          }
        }
      }
    }
  `)

  function getImagePath(name: string): string {
    const imageItem = data.allFile.edges.find(
      (element: { node: { publicURL: string } }) => {
        return element.node.publicURL.match(name)
      },
    )
    return imageItem.node.publicURL
  }

  const partnerBannerItems: Array<any> =
    data.dataJson.sideBar.bottomBanner.partnerBanner
  const clientBannerItems: Array<any> =
    data.dataJson.sideBar.bottomBanner.clientBanner

  const partnerTermsPath = data.dataJson.partnerTermsPagePath
  const clientContactPath = data.dataJson.clientContact
  const clientDocumentDownloadPath = data.dataJson.clientDocumentDownload

  // CTAの中身をそれぞれコンポーネント化
  const CtaComponents = {
    Partner: function Partner() {
      return (
        <>
          <p className="sideBar__ctaText">アフィリエイトを始めたい方</p>
          <div className="d-grid partnerCta__button with-sideBar">
            <Button href={partnerTermsPath} variant="partner">
              無料会員登録はこちら
            </Button>
          </div>
          <div className="textLink text-center mb-2">
            <Link
              to={constant.HOW_TO_JOIN_AFB_PAGE_PATH}
              className="textLink__link"
            >
              パートナー会員登録方法はこちら
            </Link>
          </div>
        </>
      )
    },
    Client: function Client() {
      return (
        <>
          <p className="sideBar__ctaText">広告出稿をご検討中の方</p>
          <div className="d-grid mb-2">
            <Button
              href={clientDocumentDownloadPath}
              variant="client-document"
              className="clientCtaBtn"
              data-cta-location="sidebarDocumentDownload"
            >
              資料をダウンロードする
            </Button>
          </div>
          <div className="d-grid">
            <Button
              href={clientContactPath}
              variant="client"
              className="clientCtaBtn"
              data-cta-location="sidebarContact"
            >
              お問い合わせ
            </Button>
          </div>
        </>
      )
    },
  }

  // gqlの結果から画像を取り出してバナーにして返す処理
  function CreateGatsbyImageBanner(item: any): React.ReactElement {
    const imageData = getImage(item.item.localImage)
    if (!imageData) {
      return <></>
    }
    return (
      <>
        <GatsbyImage
          className="sideBar__adBnrImage"
          image={imageData}
          alt={item.item.name}
        />
      </>
    )
  }

  // メニューの内容を生成する処理
  // ctaItemsには上で定義したCTAコンポーネントが入る
  let ctaItems: any
  // サイドバー下部バナー出し分け
  let bottomBnrItems: Array<any>

  // CTAの出しわけ
  if (props.userType === 'partner') {
    // パートナー時の処理
    bottomBnrItems = partnerBannerItems
    ctaItems = <CtaComponents.Partner />
  } else if (props.userType === 'client') {
    // クライアント時の処理
    ctaItems = <CtaComponents.Client />
    bottomBnrItems = clientBannerItems
  } else {
    // パートナー・クライアント以外のページ（会社概要など）
    ctaItems = (
      <>
        <CtaComponents.Partner />
        <CtaComponents.Client />
      </>
    )
    bottomBnrItems = partnerBannerItems
  }

  return (
    <div className="sideBar__body">
      <div className="sideBar__ctaBtn">{ctaItems}</div>
      {props.userType !== 'client' && (
        <Card className="shadow mb-2">
          <p className="sideBar__navTitle border-bottom">afb公式アカウント</p>
          <ul className="sideBar_snsList">
            <li className="sideBar_snsItem">
              <ExternalLink to={constant.SNS_URLS.LINE} noCssClass={true}>
                <div className="sideBar_snsIcon">
                  <img
                    src={getImagePath('ic_line')}
                    alt="LINE"
                    width="150"
                    height="150"
                    loading="lazy"
                  />
                </div>
                <span className="sideBar_snsText">LINE公式アカウント</span>
              </ExternalLink>
            </li>
            <li className="sideBar_snsItem">
              <ExternalLink to={constant.SNS_URLS.FACEBOOK} noCssClass={true}>
                <div className="sideBar_snsIcon">
                  <img
                    src={getImagePath('ic_facebook')}
                    alt="Facebook"
                    loading="lazy"
                    width="150"
                    height="150"
                  />
                </div>
                <span className="sideBar_snsText">Facebook公式アカウント</span>
              </ExternalLink>
            </li>
            <li className="sideBar_snsItem">
              <ExternalLink to={constant.SNS_URLS.INSTAGRAM} noCssClass={true}>
                <div className="sideBar_snsIcon">
                  <img
                    src={getImagePath('ic_instagram')}
                    alt="Instagram"
                    loading="lazy"
                    width="150"
                    height="150"
                  />
                </div>
                <span className="sideBar_snsText">Instagram公式アカウント</span>
              </ExternalLink>
            </li>
            <li className="sideBar_snsItem">
              <ExternalLink to={constant.SNS_URLS.X} noCssClass={true}>
                <div className="sideBar_snsIcon">
                  <img
                    src={getImagePath('ic_x')}
                    alt="X"
                    loading="lazy"
                    width="150"
                    height="150"
                  />
                </div>
                <span className="sideBar_snsText">X公式アカウント</span>
              </ExternalLink>
            </li>
          </ul>
        </Card>
      )}
      <ul className="sideBar__adBnr">
        {bottomBnrItems.map((item: any, index: number) => (
          <li className="shadow mb-2" key={`bnr${index}`}>
            <ExternalLink to={item.pageUrl} noCssClass={true}>
              {useGatsbyImage(item.useGatsbyImage) ? (
                <img
                  className="sideBar__adBnrImage"
                  src={item.imagePath}
                  alt={item.name}
                  loading="lazy"
                  width="300"
                  height="100"
                />
              ) : (
                <CreateGatsbyImageBanner item={item} />
              )}
            </ExternalLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default SideBar
